import { useEffect, useMemo, useState } from "react";
import { Star, X, Search, Check } from "lucide-react";
import { CopyableValue } from "@/components/clicks/CopyableValue";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDmcaOverrides,
  useTargetSearch,
  useAddOverride,
  useUpdateOverrideInterval,
  useRemoveOverride,
} from "@/hooks/useDmcaData";
import type { TargetSearchRow } from "@/types/dmca";

// VIP targets = per-target re-check overrides on top of the tier. Admins pin a
// high-value domain/URL to a frequent interval; the checker honours it even if
// the target is tier-3/untracked (migration 20261021100000). Cost here is ON TOP
// of the tier schedule in the card above.

const COST_PER_REQUEST = 0.0015;

// VIP leans frequent — sub-daily options plus a weekly floor.
const VIP_FREQUENCIES: { label: string; hours: number }[] = [
  { label: "Hourly", hours: 1 },
  { label: "Every 2 hours", hours: 2 },
  { label: "Every 6 hours", hours: 6 },
  { label: "Twice a day", hours: 12 },
  { label: "Once a day", hours: 24 },
  { label: "Once a week", hours: 168 },
];
const DEFAULT_VIP_HOURS = 6;

const usd = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthlyFor = (hours: number) => (24 / hours) * COST_PER_REQUEST * 30;

export function VipTargetsSettings() {
  const { data: overrides = [], isPending } = useDmcaOverrides();
  const add = useAddOverride();
  const updateInterval = useUpdateOverrideInterval();
  const remove = useRemoveOverride();

  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [notAdmin, setNotAdmin] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQuery(queryInput.trim()), 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  const { data: results = [], isFetching: searching } = useTargetSearch(query);

  const pinnedIds = useMemo(() => new Set(overrides.map((o) => o.target_id)), [overrides]);
  const freeResults = results.filter((r) => !pinnedIds.has(r.id));

  const flagNotAdmin = (rows: number) => {
    if (rows === 0) setNotAdmin(true);
    return rows > 0;
  };

  const onAdd = (r: TargetSearchRow) => {
    setNotAdmin(false);
    add.mutate(
      { targetId: r.id, intervalHours: DEFAULT_VIP_HOURS, reason: null },
      {
        onSuccess: (rows) => {
          if (flagNotAdmin(rows)) {
            setQueryInput("");
            setQuery("");
          }
        },
      },
    );
  };
  const onInterval = (id: number, hours: number) => {
    setNotAdmin(false);
    updateInterval.mutate({ id, intervalHours: hours }, { onSuccess: flagNotAdmin });
  };
  const onRemove = (id: number) => {
    setNotAdmin(false);
    remove.mutate(id, { onSuccess: flagNotAdmin });
  };

  const totalMonthly = overrides.reduce((s, o) => s + monthlyFor(o.interval_hours), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Star className="size-4" /> VIP targets
        </CardTitle>
        <CardDescription>
          Pin a high-value domain or URL to its own check interval, on top of its
          tier — for the few that must stay up to date. Checked even if the target
          is tier-3/untracked. Cost is on top of the schedule above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Add box */}
        <div className="relative max-w-xl">
          <div className="flex items-center gap-2 rounded-md border px-3 py-1.5">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <Input
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder="Add a domain or URL…"
              className="h-7 border-0 p-0 shadow-none focus-visible:ring-0"
            />
          </div>
          {query ? (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
              {freeResults.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {searching ? "Searching…" : "No matching targets (or already pinned)."}
                </div>
              ) : (
                freeResults.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onAdd(r)}
                    disabled={add.isPending}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    <span className="truncate font-medium" title={r.target}>
                      {r.target}
                    </span>
                    <span className="ml-auto flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="rounded bg-muted px-1.5 py-0.5">{r.kind}</span>
                      <span>T{r.tier}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>

        {/* List */}
        {isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : overrides.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No VIP targets yet. Search above to pin one.
          </p>
        ) : (
          <div className="space-y-1 text-sm">
            {overrides.map((o) => (
              <div
                key={o.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t py-2"
              >
                <Star className="size-3.5 shrink-0 fill-amber-400 text-amber-400" />
                <CopyableValue value={o.target} noTruncate className="flex-1 font-medium" />
                <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="rounded bg-muted px-1.5 py-0.5">{o.kind}</span>
                  <span>T{o.tier}</span>
                </span>
                <Select value={String(o.interval_hours)} onValueChange={(v) => onInterval(o.id, Number(v))}>
                  <SelectTrigger className="h-8 w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VIP_FREQUENCIES.some((f) => f.hours === o.interval_hours)
                      ? null
                      : (
                        <SelectItem value={String(o.interval_hours)}>
                          Every {o.interval_hours}h
                        </SelectItem>
                      )}
                    {VIP_FREQUENCIES.map((f) => (
                      <SelectItem key={f.hours} value={String(f.hours)}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="w-20 shrink-0 text-right tabular-nums text-muted-foreground">
                  {usd(monthlyFor(o.interval_hours))}/mo
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(o.id)}
                  disabled={remove.isPending}
                  aria-label={`Remove ${o.target}`}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between border-t-2 pt-2 font-medium">
              <span>
                {overrides.length} pinned
              </span>
              <span className="tabular-nums">+{usd(totalMonthly)}/mo</span>
            </div>
          </div>
        )}

        {notAdmin ? (
          <p className="text-sm text-destructive">Admin role required to change VIP targets.</p>
        ) : null}
        {add.isError || updateInterval.isError || remove.isError ? (
          <p className="text-sm text-destructive">Could not save — try again.</p>
        ) : null}
        {add.isSuccess && !notAdmin ? (
          <p className="inline-flex items-center gap-1 text-sm text-[var(--chart-2)]">
            <Check className="size-4" /> Pinned
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
